/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { API_PORT, RX_STOMP } from '@app/injection-tokens';
import { GmcStompError, GmcStompKnownError, GmcStompUnknownError } from '@shared/types/common';
import { RxStomp } from '@stomp/rx-stomp';
import { IFrame, StompHeaders } from '@stomp/stompjs';
import { Observable, Subject, merge } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

export type GmcStompConnectionError = { frame: IFrame; error: GmcStompError };

const maybeParseJsonErrorBody = (frame: IFrame): GmcStompError => {
  if (frame.headers['content-type'] === 'application/json') {
    try {
      return JSON.parse(frame.body) as GmcStompKnownError;
    } catch {
      return { code: null, message: frame.body } as GmcStompUnknownError;
    }
  } else {
    return { code: null, message: frame.body } as GmcStompUnknownError;
  }
};

@Injectable({ providedIn: 'root' })
export class WebSocketConnection {
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(RX_STOMP) private rxStomp: RxStomp,
    @Inject(API_PORT) private apiPort: string | null,
  ) {
    const url = new URL(this.document.location.href);
    url.port = this.apiPort ?? url.port;
    url.protocol = url.protocol === 'https:' ? 'wss' : 'ws';
    url.pathname = 'browsers';
    this.rxStomp.configure({
      brokerURL: url.href,
      reconnectDelay: 200,
    });
    this.deactivate$.subscribe(() => this.rxStomp.deactivate());
  }

  private deactivate$ = new Subject<true>();
  private topicReceipts = new WeakMap<Observable<unknown>, string>();

  get errors$(): Observable<GmcStompConnectionError> {
    return this.rxStomp.stompErrors$.pipe(
      map((frame) => ({
        frame,
        error: frame.body
          ? (maybeParseJsonErrorBody(frame) as GmcStompKnownError)
          : ({ code: null, message: null } as GmcStompUnknownError),
      })),
    );
  }

  watchForReceipt(receipt: string, callback: () => void) {
    this.rxStomp.watchForReceipt(receipt, callback);
  }

  public topicSubscribed<T>(fn: () => void) {
    return (source: Observable<T>) => {
      const receipt = this.topicReceipts.get(source);
      if (receipt) this.watchForReceipt(receipt, fn);
      return source;
    };
  }

  getTopic<T>(topic: string, headers: StompHeaders = {}): Observable<T> {
    const subscriptionId = uuid();
    headers.id = subscriptionId;
    if (!headers.receipt) headers = { ...headers, receipt: uuid() };
    const result = this.rxStomp.watch(topic, headers).pipe(
      filter((frame) => frame.headers.subscription === subscriptionId),
      map((frame) => JSON.parse(frame.body)),
      takeUntil(
        merge(
          this.deactivate$,
          this.errors$.pipe(filter((error) => error.frame.headers['receipt-id'] === headers.receipt)),
          this.errors$.pipe(filter((message) => message.error.code === 'err.cluster-is-not-attached')),
        ),
      ),
    );
    this.topicReceipts.set(result, headers.receipt!);
    return result;
  }

  send(topic: string, body: any, headers?: StompHeaders): void {
    return this.rxStomp.publish({ destination: topic, body: JSON.stringify(body), headers });
  }

  activate() {
    this.rxStomp.activate();
  }

  deactivate() {
    this.deactivate$.next(true);
  }
}
